import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { EmailLogEntry } from '@vedjegy/shared';

const EVENT_LABELS: Record<string, string> = {
  application_submitted: 'Pályázat beküldve',
  preq_passed:           'Előminősítés: megfelelt',
  preq_failed:           'Előminősítés: nem felelt meg',
  under_review:          'Bírálat megkezdve',
  deficiency_required:   'Hiánypótlás felszólítás',
  deficiency_submitted:  'Hiánypótlás teljesítve',
  approved:              'Pályázat elfogadva',
  rejected:              'Pályázat elutasítva',
  invite_sent:           'Munkatárs meghívása',
  conflict_reported:     'Összeférhetetlenség (admin)',
  conflict_confirmed:    'Összeférhetetlenség (bíráló)',
};

const STATUS_COLOR: Record<string, string> = {
  sent:    '#1a7a40',
  failed:  '#c0392b',
  pending: '#b07d00',
};
const STATUS_BG: Record<string, string> = {
  sent:    'rgba(78,192,124,.12)',
  failed:  'rgba(209,49,46,.1)',
  pending: 'rgba(234,219,135,.3)',
};
const STATUS_LABEL: Record<string, string> = {
  sent:    'Elküldve',
  failed:  'Sikertelen',
  pending: 'Függőben',
};

const PAGE_SIZE = 50;

export default function AdminEmailLogPage() {
  const [logs, setLogs]       = useState<EmailLogEntry[]>([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEvent, setFilterEvent]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.admin.getEmailLog({
        status: filterStatus || undefined,
        event:  filterEvent  || undefined,
        limit:  PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setLogs(res.logs);
      setTotal(res.pagination.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Betöltési hiba.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterEvent, page]);

  useEffect(() => { void load(); }, [load]);

  // Reset to page 0 when filters change
  function applyFilter(newStatus: string, newEvent: string) {
    setPage(0);
    setFilterStatus(newStatus);
    setFilterEvent(newEvent);
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminLayout>
      <div style={{ padding: '32px 40px' }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>E-mail napló</h1>
          <div style={{ fontSize: 13, color: 'var(--muted-foreground)', marginTop: 4 }}>
            Kiküldött értesítések és kézbesítési státusz
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <select
            value={filterStatus}
            onChange={(e) => applyFilter(e.target.value, filterEvent)}
            style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 13, background: '#fff' }}
          >
            <option value="">Minden státusz</option>
            <option value="sent">Elküldve</option>
            <option value="failed">Sikertelen</option>
            <option value="pending">Függőben</option>
          </select>
          <select
            value={filterEvent}
            onChange={(e) => applyFilter(filterStatus, e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 13, background: '#fff' }}
          >
            <option value="">Minden esemény</option>
            {Object.entries(EVENT_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={() => void load()}>↻ Frissítés</button>
          <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--muted-foreground)', alignSelf: 'center' }}>
            {total} bejegyzés
          </div>
        </div>

        {error && (
          <div className="alert alert-destructive" style={{ marginBottom: 16 }}>
            <span>⚠</span><div><p>{error}</p></div>
          </div>
        )}

        {/* Table */}
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 14 }}>Betöltés...</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 14 }}>Nincs találat.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 700, fontSize: 11, color: 'var(--secondary-foreground)' }}>Esemény</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 700, fontSize: 11, color: 'var(--secondary-foreground)' }}>Cég</th>
                  <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 700, fontSize: 11, color: 'var(--secondary-foreground)' }}>Címzett</th>
                  <th style={{ textAlign: 'center', padding: '10px 16px', fontWeight: 700, fontSize: 11, color: 'var(--secondary-foreground)' }}>Státusz</th>
                  <th style={{ textAlign: 'center', padding: '10px 16px', fontWeight: 700, fontSize: 11, color: 'var(--secondary-foreground)' }}>Próbálkozás</th>
                  <th style={{ textAlign: 'right', padding: '10px 16px', fontWeight: 700, fontSize: 11, color: 'var(--secondary-foreground)' }}>Időpont</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}
                    title={log.lastError ?? undefined}>
                    <td style={{ padding: '10px 16px', color: 'var(--primary)', fontWeight: 500 }}>
                      {EVENT_LABELS[log.event] ?? log.event}
                    </td>
                    <td style={{ padding: '10px 16px', color: 'var(--muted-foreground)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.companyName ?? '–'}
                    </td>
                    <td style={{ padding: '10px 16px', color: 'var(--muted-foreground)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.recipientEmail}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                        background: STATUS_BG[log.status] ?? 'var(--muted)',
                        color: STATUS_COLOR[log.status] ?? 'var(--muted-foreground)',
                      }}>
                        {STATUS_LABEL[log.status] ?? log.status}
                      </span>
                      {log.status === 'failed' && log.lastError && (
                        <div style={{ fontSize: 10, color: '#c0392b', marginTop: 3, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.lastError}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', color: log.attempts > 1 ? '#b07d00' : 'var(--muted-foreground)' }}>
                      {log.attempts}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
                      {log.sentAt
                        ? new Date(log.sentAt).toLocaleString('hu-HU')
                        : new Date(log.createdAt).toLocaleString('hu-HU')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
            <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>‹ Előző</button>
            <span style={{ fontSize: 13, color: 'var(--muted-foreground)', alignSelf: 'center' }}>
              {page + 1} / {totalPages}
            </span>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Következő ›</button>
          </div>
        )}

      </div>
    </AdminLayout>
  );
}
